<template>
    <div class="artigos-calendar">
        <v-container class="pt-6 pb-3">
            <div class="cal-toolbar d-flex flex-wrap align-center">
                <div class="d-flex align-center">
                    <v-btn icon small @click="prev"><v-icon>mdi-chevron-left</v-icon></v-btn>
                    <div class="cal-month mx-2">{{ monthTitle }}</div>
                    <v-btn icon small @click="next"><v-icon>mdi-chevron-right</v-icon></v-btn>
                    <v-btn text small class="ml-1" @click="today">Hoje</v-btn>
                </div>

                <div class="cal-view-switch ml-3">
                    <button
                        v-for="v in viewOptions"
                        :key="v.value"
                        type="button"
                        class="cal-view-btn"
                        :class="{ 'is-active': calType === v.value }"
                        @click="calType = v.value"
                    >
                        <v-icon x-small class="mr-1">{{ v.icon }}</v-icon>{{ v.label }}
                    </button>
                </div>

                <v-spacer />

                <div class="d-flex align-center flex-wrap" style="gap:8px">
                    <span class="plannable-hint" v-if="!loadingPlannable && plannable.length > 0">
                        <v-icon small class="mr-1">mdi-lightbulb-on-outline</v-icon>
                        {{ plannable.length }} tema{{ plannable.length === 1 ? '' : 's' }} pronto{{ plannable.length === 1 ? '' : 's' }} para agendar
                    </span>
                    <span class="plannable-hint plannable-hint--empty" v-else-if="plannableEmpty && schedules.length > 0">
                        <v-icon small class="mr-1" color="warning">mdi-lightbulb-off-outline</v-icon>
                        Você já usou todos os temas escolhidos
                    </span>
                    <v-btn v-if="clearableCount > 0" text small color="error" @click="clearOpen = true">
                        <v-icon left small>mdi-calendar-remove-outline</v-icon> Limpar planejamento
                    </v-btn>
                    <v-btn text small @click="settingsOpen = true">
                        <v-icon left small>mdi-cog-outline</v-icon> Preferências
                    </v-btn>
                    <v-btn v-if="schedules.length > 0 && !plannableEmpty" color="primary" :disabled="plannable.length === 0" @click="openWizard">
                        <v-icon left size="18">mdi-calendar-plus</v-icon> Agendar mais artigos
                    </v-btn>
                    <v-tooltip bottom v-else-if="schedules.length > 0">
                        <template #activator="{ on, attrs }">
                            <v-btn color="primary" v-on="on" v-bind="attrs" @click="goChooseThemes">
                                <v-icon left size="18">mdi-lightbulb-multiple-outline</v-icon> Escolher mais temas
                            </v-btn>
                        </template>
                        <span>Marque temas como "Quero usar" na área Criar artigo para poder agendar novos posts.</span>
                    </v-tooltip>
                </div>
            </div>

            <glass-panel v-if="!loading && schedules.length === 0" padding="8" class="cal-empty mt-4">
                <div class="cal-empty__icon">
                    <v-icon size="34" color="primary">mdi-calendar-heart-outline</v-icon>
                </div>
                <h2 class="cal-empty__title">Seu site publicando sozinho, no piloto automático</h2>
                <p class="cal-empty__desc">
                    Escolha os dias, nós escrevemos os artigos e você só aprova por e-mail.
                    Comece marcando temas como "Quero usar" na área <strong>Criar artigo</strong>.
                </p>
                <div class="cal-empty__actions">
                    <v-btn v-if="plannableEmpty" color="primary" large @click="goChooseThemes">
                        <v-icon left size="18">mdi-lightbulb-multiple-outline</v-icon> Escolher temas
                    </v-btn>
                    <v-btn v-else color="primary" large :disabled="plannable.length === 0" @click="openWizard">
                        <v-icon left size="18">mdi-calendar-plus</v-icon> Ativar publicação automática
                    </v-btn>
                    <v-btn text color="primary" @click="$emit('open-guide', 'auto')">Como funciona?</v-btn>
                </div>
            </glass-panel>
        </v-container>

        <v-container class="pt-0 pb-12">
            <div class="cal-wrap" :class="`cal-wrap--${calType}`">
                <v-calendar
                    ref="calendar"
                    v-model="focus"
                    :type="calType"
                    :events="events"
                    :event-color="getEventColor"
                    event-more-text="+ {0} mais"
                    locale="pt-BR"
                    color="primary"
                    :first-time="'06:00'"
                    :interval-minutes="60"
                    :interval-count="17"
                    :interval-height="42"
                    @click:event="showEvent"
                    @click:more="goToDay"
                    @click:date="goToDay"
                />
            </div>

            <!-- Legenda -->
            <div class="cal-legend mt-4">
                <span v-for="l in legend" :key="l.status" class="legend-item">
                    <span class="legend-dot" :style="{ background: l.color }"></span>{{ l.label }}
                </span>
            </div>
        </v-container>

        <!-- Detalhe do slot -->
        <glass-dialog v-model="detailOpen" :title="selected ? (selected.theme_snapshot && selected.theme_snapshot.title) || 'Artigo agendado' : ''" :max-width="500">
            <template v-if="selected">
                <div class="detail-meta mb-3">
                    <span :class="['pill', 'detail-pill']" :style="{ '--pill-color': meta.color }">
                        <v-icon left small :color="meta.color">{{ meta.icon }}</v-icon>{{ meta.label }}
                    </span>
                    <span class="detail-date">
                        <v-icon small class="mr-1">mdi-calendar-clock</v-icon>{{ formatFull(selected.scheduled_date) }}
                    </span>
                </div>

                <div v-if="timelineStage > 0" class="detail-steps mb-3" aria-label="Etapas da publicação">
                    <div
                        v-for="(stepLabel, i) in timelineLabels"
                        :key="stepLabel"
                        class="detail-step"
                        :class="{
                            'detail-step--done': i + 1 < timelineStage || (timelineStage === 4 && i === 3),
                            'detail-step--current': i + 1 === timelineStage && timelineStage < 4,
                        }"
                    >
                        <span class="detail-step__dot">
                            <v-icon v-if="i + 1 < timelineStage || (timelineStage === 4 && i === 3)" size="12" color="success">mdi-check-bold</v-icon>
                        </span>
                        <span class="detail-step__label">{{ stepLabel }}</span>
                    </div>
                </div>

                <p v-if="selected.theme_snapshot && selected.theme_snapshot.keyword" class="body-sm ma-0 mb-2">
                    <strong>Palavra-chave:</strong> {{ selected.theme_snapshot.keyword }}
                </p>

                <div v-if="selected.status === 'planned'" class="detail-alert">
                    <v-icon small color="primary" class="mr-1">mdi-clock-outline</v-icon>
                    Este artigo será escrito automaticamente 2 dias antes da data. Depois, você recebe um e-mail para aprovar.
                </div>

                <div v-if="selected.status === 'failed'" class="detail-alert detail-alert--error">
                    <v-icon small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
                    {{ failureText }}
                </div>

                <div v-if="selected.status === 'generating'" class="detail-alert">
                    <v-icon small color="primary" class="mr-1">mdi-progress-clock</v-icon>
                    Estamos escrevendo este artigo. Leva só alguns minutos.
                </div>

                <div v-if="selected.status === 'awaiting_review'" class="detail-alert">
                    <v-icon small color="warning" class="mr-1">mdi-email-open-outline</v-icon>
                    O artigo está pronto e esperando a sua leitura. Aprove para ele ir ao ar na data marcada.
                </div>

                <div v-if="selected.status === 'skipped'" class="detail-alert">
                    <v-icon small class="mr-1">mdi-information-outline</v-icon>
                    Não foi publicado porque não houve aprovação até a data. O artigo continua salvo em "Meus artigos" — você pode publicá-lo por lá.
                </div>

                <div v-if="['approved', 'auto_approved'].includes(selected.status)" class="detail-alert detail-alert--ok">
                    <v-icon small color="success" class="mr-1">mdi-check-circle-outline</v-icon>
                    Aprovado! Vai para o seu site sozinho na data marcada.
                </div>

                <!-- [admin] Simula as etapas do fluxo automático sem esperar as janelas de data -->
                <div v-if="isAdmin && (adminActions.length > 0 || adminShowReviewLink)" class="admin-test mt-3">
                    <div class="admin-test__title">
                        <v-icon size="14" class="mr-1">mdi-test-tube</v-icon> Testes (admin)
                    </div>
                    <div class="admin-test__buttons">
                        <v-btn
                            v-for="a in adminActions"
                            :key="a.action"
                            x-small outlined
                            :loading="busy && simulating === a.action"
                            :disabled="busy && simulating !== a.action"
                            @click="simulate(a.action)"
                        >
                            <v-icon left x-small>{{ a.icon }}</v-icon>{{ a.label }}
                        </v-btn>
                        <v-btn v-if="adminShowReviewLink" x-small outlined @click="openReviewLink(false)">
                            <v-icon left x-small>mdi-open-in-app</v-icon> Abrir revisão (link do email)
                        </v-btn>
                        <v-btn v-if="adminShowEditLink" x-small outlined @click="openReviewLink(true)">
                            <v-icon left x-small>mdi-pencil-outline</v-icon> Abrir edição (link do email)
                        </v-btn>
                    </div>
                </div>

                <div v-if="deleteConfirm" class="detail-alert detail-alert--error mt-2">
                    {{ deleteMessage }}
                </div>
            </template>

            <template #actions>
                <template v-if="selected && !deleteConfirm">
                    <v-btn
                        v-if="canDelete"
                        text color="error"
                        :disabled="busy"
                        @click="deleteConfirm = true"
                    >Excluir</v-btn>

                    <v-spacer />

                    <v-btn
                        v-if="canGenerateNow"
                        color="primary"
                        :loading="busy"
                        @click="generateNow"
                    >
                        <v-icon left>mdi-flash-outline</v-icon> Criar agora
                    </v-btn>

                    <v-btn
                        v-if="selected.status === 'awaiting_review' && selected.article"
                        color="primary"
                        @click="goReview"
                    >
                        <v-icon left>mdi-eye-check-outline</v-icon> Revisar e aprovar
                    </v-btn>

                    <v-btn
                        v-if="selected.status === 'published' && selected.article && selected.article.duda_link"
                        color="primary"
                        :href="selected.article.duda_link"
                        target="_blank"
                    >
                        <v-icon left>mdi-open-in-new</v-icon> Ver no site
                    </v-btn>
                </template>

                <template v-else-if="selected && deleteConfirm">
                    <v-btn text :disabled="busy" @click="deleteConfirm = false">Cancelar</v-btn>
                    <v-spacer />
                    <v-btn color="error" :loading="busy" @click="doDelete">Confirmar exclusão</v-btn>
                </template>
            </template>
        </glass-dialog>

        <ArtigosPlanWizard v-model="wizardOpen" :plannable-themes="plannable" @created="onPlanCreated" />
        <ArtigosSettingsDialog v-model="settingsOpen" @saved="onSettingsSaved" />

        <!-- Limpar planejamento (de hoje em diante) -->
        <glass-dialog v-model="clearOpen" title="Limpar planejamento?" :max-width="500">
            <p class="body-sm ma-0 mb-2">
                {{ clearableCount === 1 ? '1 agendamento de hoje em diante será removido' : `${clearableCount} agendamentos de hoje em diante serão removidos` }}
                do calendário. O que já foi publicado permanece como histórico.
            </p>
            <div class="detail-alert detail-alert--error">
                <v-icon small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
                Artigos que já foram escritos continuam salvos em "Meus artigos".
            </div>
            <template #actions>
                <v-btn text :disabled="clearBusy" @click="clearOpen = false">Cancelar</v-btn>
                <v-spacer />
                <v-btn color="error" :loading="clearBusy" @click="doClearAll">Limpar planejamento</v-btn>
            </template>
        </glass-dialog>
    </div>
</template>

<script>
import Api from '@/lib/Api'
import { emitToastr } from '@/lib/Utils'
import ArtigosPlanWizard from '@/components/ArtigosPlanWizard.vue'
import ArtigosSettingsDialog from '@/components/ArtigosSettingsDialog.vue'

const STATUS_META = {
    planned: { label: 'Na fila', color: '#702ae1', icon: 'mdi-calendar-blank-outline' },
    generating: { label: 'Escrevendo…', color: '#2f80ed', icon: 'mdi-progress-clock' },
    awaiting_review: { label: 'Esperando sua revisão', color: '#f2a20c', icon: 'mdi-clock-alert-outline' },
    approved: { label: 'Aprovado', color: '#12a150', icon: 'mdi-check' },
    auto_approved: { label: 'Aprovado automaticamente', color: '#12a150', icon: 'mdi-check-all' },
    published: { label: 'No seu site', color: '#0e7a3c', icon: 'mdi-check-circle' },
    skipped: { label: 'Não publicado', color: '#98a2b3', icon: 'mdi-cancel' },
    failed: { label: 'Precisa de atenção', color: '#e5484d', icon: 'mdi-alert-circle-outline' },
}

// Etapa atual na linha do tempo do detalhe (0 = não se aplica: failed/skipped).
const TIMELINE_STAGE = {
    planned: 1,
    generating: 1,
    awaiting_review: 2,
    approved: 3,
    auto_approved: 3,
    published: 4,
}

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']

const POLL_MS = 15000

export default {
    name: 'ArtigosCalendar',
    components: { ArtigosPlanWizard, ArtigosSettingsDialog },
    data() {
        return {
            focus: this.toISODate(new Date()),
            calType: 'month',
            viewOptions: [
                { value: 'month', label: 'Mês', icon: 'mdi-calendar-month-outline' },
                { value: 'week', label: 'Semana', icon: 'mdi-calendar-week-outline' },
            ],
            schedules: [],
            plannable: [],
            loading: false,
            loadingPlannable: false,
            wizardOpen: false,
            settingsOpen: false,
            settings: null,
            pendingWizard: false,
            detailOpen: false,
            selected: null,
            deleteConfirm: false,
            busy: false,
            clearOpen: false,
            clearBusy: false,
            // [admin] etapa em simulação e prazo de polling forçado (make_due_generation)
            simulating: null,
            forcePollUntil: 0,
            pollTimer: null,
            legend: [
                { status: 'planned', label: STATUS_META.planned.label, color: STATUS_META.planned.color },
                { status: 'awaiting_review', label: STATUS_META.awaiting_review.label, color: STATUS_META.awaiting_review.color },
                { status: 'approved', label: STATUS_META.approved.label, color: STATUS_META.approved.color },
                { status: 'published', label: STATUS_META.published.label, color: STATUS_META.published.color },
                { status: 'failed', label: STATUS_META.failed.label, color: STATUS_META.failed.color },
            ],
        }
    },
    computed: {
        events() {
            return (this.schedules || []).map(s => {
                const m = STATUS_META[s.status] || STATUS_META.planned
                const start = new Date(s.scheduled_date)
                return {
                    name: (s.theme_snapshot && s.theme_snapshot.title) || 'Artigo',
                    start,
                    end: new Date(start.getTime() + 60 * 60 * 1000),
                    color: m.color,
                    icon: m.icon,
                    timeLabel: `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`,
                    timed: true,
                    raw: s,
                }
            })
        },
        monthTitle() {
            const d = this.focus ? new Date(this.focus + 'T00:00:00') : new Date()
            return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`
        },
        meta() {
            return this.selected ? (STATUS_META[this.selected.status] || STATUS_META.planned) : STATUS_META.planned
        },
        timelineLabels() {
            return ['Escrever', 'Você revisa', 'Aprovado', 'No site']
        },
        timelineStage() {
            return this.selected ? (TIMELINE_STAGE[this.selected.status] || 0) : 0
        },
        canDelete() {
            return this.selected && this.selected.status !== 'published'
        },
        // Agendamentos de hoje em diante que podem ser limpos (o que já foi, foi).
        clearableCount() {
            const start = new Date()
            start.setHours(0, 0, 0, 0)
            return (this.schedules || []).filter(s => s.status !== 'published' && new Date(s.scheduled_date) >= start).length
        },
        hasGenerating() {
            return (this.schedules || []).some(s => s.status === 'generating')
        },
        // Sem tema disponível (todos já agendados/usados ou nenhum marcado como
        // "Quero usar"): o CTA vira "Escolher temas" e leva à aba Criar artigo.
        plannableEmpty() {
            return !this.loadingPlannable && this.plannable.length === 0
        },
        settingsComplete() {
            const s = this.settings
            if (!s) return false
            return !!String(s.default_article_author || '').trim() && !!String(s.default_article_image || '').trim()
        },
        deleteMessage() {
            if (!this.selected) return ''
            if (this.selected.article) {
                return 'Este artigo já foi gerado. Ele será removido do calendário, mas continuará salvo em "Meus artigos" e o crédito não será devolvido.'
            }
            return 'Este post ainda não foi gerado. Ele será removido do calendário e o tema voltará a ficar disponível. Nenhum crédito é consumido.'
        },
        failureText() {
            if (!this.selected) return ''
            if (this.selected.generation_error === 'no_credits') {
                return 'Seus créditos acabaram na hora de criar este artigo. Quando o saldo voltar, ele será criado sozinho — ou toque em "Criar agora".'
            }
            if (this.selected.generation_error === 'publish_failed') {
                return 'O artigo foi escrito, mas não conseguimos publicá-lo no seu site. Abra-o em "Meus artigos" para publicar ou reagendar.'
            }
            return `Algo deu errado na criação: ${this.selected.generation_error || 'erro desconhecido'}.`
        },
        // Falha de publicação: o artigo já existe (e já custou crédito) — não faz
        // sentido oferecer "Gerar agora".
        canGenerateNow() {
            if (!this.selected) return false
            if (!['planned', 'failed'].includes(this.selected.status)) return false
            return !(this.selected.generation_error === 'publish_failed' && this.selected.article)
        },
        isAdmin() {
            return this.$store.getters.isAdmin
        },
        // [admin] Botões de simulação do fluxo automático conforme o status do slot.
        adminActions() {
            if (!this.selected) return []
            const s = this.selected
            const actions = []
            if (s.status === 'planned' || (s.status === 'failed' && s.generation_error === 'no_credits')) {
                actions.push({ action: 'make_due_generation', icon: 'mdi-robot-outline', label: 'Robô: gerar via fila (≤1 min)' })
            }
            if (s.status === 'awaiting_review' && s.article) {
                actions.push({ action: 'send_review_email', icon: 'mdi-email-fast-outline', label: 'Enviar 1º email de revisão' })
                actions.push({ action: 'send_second_review_email', icon: 'mdi-email-sync-outline', label: 'Enviar 2º email (lembrete)' })
                actions.push({ action: 'make_due_review_deadline', icon: 'mdi-calendar-alert', label: 'Simular prazo sem revisão' })
            }
            if (['approved', 'auto_approved'].includes(s.status) && s.article) {
                actions.push({ action: 'make_due_publish', icon: 'mdi-rocket-launch-outline', label: 'Robô: publicar agora (Duda real)' })
            }
            return actions
        },
        adminShowReviewLink() {
            if (!this.selected || !this.selected.article) return false
            return ['awaiting_review', 'approved', 'auto_approved'].includes(this.selected.status)
        },
        adminShowEditLink() {
            return this.adminShowReviewLink && this.selected.status === 'awaiting_review'
        },
    },
    methods: {
        toISODate(date) {
            const y = date.getFullYear()
            const m = String(date.getMonth() + 1).padStart(2, '0')
            const d = String(date.getDate()).padStart(2, '0')
            return `${y}-${m}-${d}`
        },
        getEventColor(event) {
            return event.color
        },
        prev() {
            this.$refs.calendar.prev()
        },
        next() {
            this.$refs.calendar.next()
        },
        today() {
            this.focus = this.toISODate(new Date())
        },
        goToDay(day) {
            if (day && day.date) {
                this.focus = day.date
                this.calType = 'week'
            }
        },
        formatFull(date) {
            if (!date) return ''
            return this.$date.format(new Date(date), 'dd/MM/yyyy HH:mm')
        },
        showEvent({ event }) {
            this.selected = event.raw
            this.deleteConfirm = false
            this.detailOpen = true
        },
        goChooseThemes() {
            this.$emit('go', 'criar')
        },
        openWizard() {
            // Só permite planejar com autor e imagem padrão definidos. Se faltarem,
            // abre primeiro as Configurações e, ao salvar, retoma o planejamento.
            if (!this.settingsComplete) {
                this.pendingWizard = true
                this.settingsOpen = true
                emitToastr('info', 'Quase lá', 'Antes de ativar, defina o autor e a imagem padrão das publicações.')
                return
            }
            this.wizardOpen = true
        },
        async loadSettings() {
            try {
                const resp = await Api.getArticleSettings()
                if (!resp?.error) this.settings = resp.message
            } catch (err) {
                // Silencioso: se falhar, o clique em "Novo planejamento" abrirá as Configurações.
            }
        },
        onSettingsSaved(payload) {
            if (payload) this.settings = payload
            if (this.pendingWizard) {
                this.pendingWizard = false
                // O save só conclui com autor + imagem preenchidos, então já está completo.
                if (this.settingsComplete) {
                    this.$nextTick(() => { this.wizardOpen = true })
                }
            }
        },
        async loadCalendar() {
            this.loading = true
            try {
                const resp = await Api.getCalendar({})
                if (!resp?.error) {
                    this.schedules = resp.message.schedules || []
                }
            } catch (err) {
                emitToastr('error', err?.message || 'Erro ao carregar calendário.')
            } finally {
                this.loading = false
                this.syncPolling()
            }
        },
        // Enquanto houver slot em geração (disparada pelo cron), recarrega a cada 15s.
        // forcePollUntil mantém o polling após simulações que dependem do próximo tick do cron.
        syncPolling() {
            if (this.hasGenerating || Date.now() < this.forcePollUntil) {
                if (!this.pollTimer) this.pollTimer = setInterval(() => this.loadCalendar(), POLL_MS)
            } else {
                this.stopPolling()
            }
        },
        stopPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer)
                this.pollTimer = null
            }
        },
        async loadPlannable() {
            this.loadingPlannable = true
            try {
                const resp = await Api.getPlannableThemes()
                if (!resp?.error) {
                    this.plannable = resp.message.themes || []
                }
            } catch (err) {
                this.plannable = []
            } finally {
                this.loadingPlannable = false
            }
        },
        async onPlanCreated() {
            await Promise.all([this.loadCalendar(), this.loadPlannable()])
        },
        async generateNow() {
            if (!this.selected || this.busy) return
            this.busy = true
            try {
                const resp = await Api.generatePlannedArticleNow(this.selected._id)
                if (resp?.error) {
                    emitToastr('error', resp.message || 'Erro ao gerar artigo.')
                    await this.loadCalendar()
                    return
                }
                emitToastr('success', 'Artigo criado!', 'Enviamos um e-mail para você revisar e aprovar.')
                this.detailOpen = false
                await this.loadCalendar()
            } catch (err) {
                emitToastr('error', err?.message || 'Erro ao gerar artigo.')
            } finally {
                this.busy = false
            }
        },
        async doDelete() {
            if (!this.selected || this.busy) return
            this.busy = true
            try {
                const resp = await Api.deleteSchedule(this.selected._id)
                if (resp?.error) {
                    emitToastr('error', resp.message || 'Erro ao excluir.')
                    return
                }
                emitToastr('success', 'Post removido do calendário.')
                this.detailOpen = false
                this.deleteConfirm = false
                await Promise.all([this.loadCalendar(), this.loadPlannable()])
            } catch (err) {
                emitToastr('error', err?.message || 'Erro ao excluir.')
            } finally {
                this.busy = false
            }
        },
        async doClearAll() {
            if (this.clearBusy) return
            this.clearBusy = true
            try {
                // Início do dia no fuso do usuário (mesmo padrão do create-plan).
                const from = new Date()
                from.setHours(0, 0, 0, 0)
                const resp = await Api.clearSchedules({ from: from.toISOString() })
                if (resp?.error) {
                    emitToastr('error', resp.message || 'Erro ao limpar o planejamento.')
                    return
                }
                const cleared = resp.message?.cleared || 0
                emitToastr('success', 'Planejamento limpo.', `${cleared} agendamento${cleared === 1 ? '' : 's'} removido${cleared === 1 ? '' : 's'} do calendário.`)
                this.clearOpen = false
                await Promise.all([this.loadCalendar(), this.loadPlannable()])
            } catch (err) {
                emitToastr('error', err?.message || 'Erro ao limpar o planejamento.')
            } finally {
                this.clearBusy = false
            }
        },
        goReview() {
            if (!this.selected || !this.selected.article) return
            const articleId = this.selected.article._id || this.selected.article
            this.$router.push(`/articles/review/${articleId}`)
        },
        // [admin] Dispara uma etapa do fluxo automático via /simulate-schedule-step.
        async simulate(action) {
            if (!this.selected || this.busy) return
            this.busy = true
            this.simulating = action
            try {
                const resp = await Api.simulateScheduleStep(this.selected._id, action)
                if (resp?.error) {
                    emitToastr('error', resp.message || 'Erro na simulação.')
                    return
                }
                emitToastr('success', 'Etapa executada.', resp.message?.info)
                // Geração acontece no próximo tick do cron: mantém o polling por 3 min.
                if (action === 'make_due_generation') {
                    this.forcePollUntil = Date.now() + 3 * 60 * 1000
                }
                const id = this.selected._id
                await this.loadCalendar()
                // Re-sincroniza o slot aberto para o dialog refletir o novo status.
                const fresh = (this.schedules || []).find(x => x._id === id)
                if (fresh) this.selected = fresh
            } catch (err) {
                emitToastr('error', err?.message || 'Erro na simulação.')
            } finally {
                this.busy = false
                this.simulating = null
            }
        },
        // [admin] Abre a mesma rota dos botões do email de revisão (aprovar / editar).
        openReviewLink(edit) {
            if (!this.selected || !this.selected.article) return
            const articleId = this.selected.article._id || this.selected.article
            const route = { path: `/articles/review/${articleId}` }
            if (edit) route.query = { edit: '1' }
            this.$router.push(route)
        },
    },
    watch: {
        // Cancelar as Configurações (fechar sem salvar) cancela o planejamento pendente.
        settingsOpen(val) {
            if (!val && this.pendingWizard) this.pendingWizard = false
        },
    },
    mounted() {
        this.loadCalendar()
        this.loadPlannable()
        this.loadSettings()
    },
    beforeDestroy() {
        this.stopPolling()
    },
}
</script>

<style scoped>
.cal-toolbar {
    gap: 10px;
}

.cal-month {
    font-weight: 800;
    font-size: 18px;
    color: var(--color-on-surface);
    text-transform: capitalize;
    min-width: 150px;
    text-align: center;
}

.plannable-hint {
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    color: var(--color-on-surface-variant);
    margin-right: 4px;
}

.plannable-hint--empty {
    color: #a15c07;
    font-weight: 600;
}

.cal-empty {
    text-align: center;
}

.cal-empty__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 9999px;
    background: rgba(112, 42, 225, 0.09);
    border: 1px solid rgba(112, 42, 225, 0.18);
    margin-bottom: 14px;
}

.cal-empty__title {
    font-size: 21px;
    font-weight: 800;
    color: var(--color-on-surface);
    margin: 0 0 8px;
}

.cal-empty__desc {
    font-size: 14px;
    line-height: 1.6;
    color: var(--color-on-surface-variant);
    max-width: 520px;
    margin: 0 auto 18px;
}

.cal-empty__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 10px;
}

.detail-steps {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    padding: 12px 8px;
    border-radius: var(--radius-sm, 12px);
    background: var(--glass-soft-bg, rgba(255, 255, 255, 0.4));
    border: 1px solid var(--glass-panel-border);
}

.detail-step {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    position: relative;
}

/* conector entre etapas */
.detail-step + .detail-step::before {
    content: '';
    position: absolute;
    top: 9px;
    right: 50%;
    width: 100%;
    height: 2px;
    background: var(--glass-panel-border, rgba(112, 105, 161, 0.22));
    transform: translateX(-9px);
    z-index: 0;
}

.detail-step--done + .detail-step::before,
.detail-step--done + .detail-step--done::before,
.detail-step--current::before {
    background: var(--color-primary, #702ae1) !important;
}

.detail-step__dot {
    position: relative;
    z-index: 1;
    width: 18px;
    height: 18px;
    border-radius: 9999px;
    border: 2px solid var(--glass-panel-border, rgba(112, 105, 161, 0.3));
    background: var(--color-surface-container-lowest, #fff);
    display: flex;
    align-items: center;
    justify-content: center;
}

.detail-step--done .detail-step__dot {
    border-color: var(--v-success-base, #12a150);
    background: rgba(18, 161, 80, 0.1);
}

.detail-step--current .detail-step__dot {
    border-color: var(--color-primary, #702ae1);
    box-shadow: 0 0 0 4px rgba(112, 42, 225, 0.15);
}

.detail-step__label {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-align: center;
    color: var(--color-on-surface-variant);
}

.detail-step--current .detail-step__label {
    color: var(--color-primary, #702ae1);
}

.cal-view-switch {
    display: inline-flex;
    background: var(--glass-soft-bg);
    border: 1px solid var(--glass-panel-border);
    border-radius: var(--radius-pill);
    padding: 3px;
    gap: 2px;
}

.cal-view-btn {
    display: inline-flex;
    align-items: center;
    border: none;
    background: transparent;
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--color-on-surface-variant);
    padding: 5px 14px;
    border-radius: var(--radius-pill);
    cursor: pointer;
    transition: all 160ms ease;
}

.cal-view-btn.is-active {
    background: var(--color-surface-container-lowest, #fff);
    color: var(--color-primary);
    box-shadow: var(--shadow-soft, 0 1px 3px rgba(16, 24, 40, 0.08));
}

.cal-wrap {
    border-radius: var(--radius-lg);
    overflow: hidden;
    border: 1px solid var(--glass-panel-border);
    background: var(--color-surface-container-lowest, #fff);
    box-shadow: var(--shadow-soft, 0 2px 10px rgba(16, 24, 40, 0.05));
    /* altura DEFINIDA (não min-height) para o v-calendar preencher com height:100% */
    height: calc(100vh - 300px);
    min-height: 640px;
}

/* Garante que o calendário ocupe toda a altura do container */
.cal-wrap >>> .v-calendar {
    height: 100%;
}

.cal-wrap >>> .v-calendar-weekly__day-label .v-btn {
    font-weight: 700;
}

.cal-wrap >>> .v-calendar-weekly__head-weekday {
    font-weight: 700;
    letter-spacing: 0.04em;
    color: var(--color-on-surface-variant);
    text-transform: capitalize;
    padding: 8px 0;
}

.cal-wrap >>> .v-calendar-weekly__day.v-present .v-calendar-weekly__day-label .v-btn {
    background: var(--color-primary) !important;
    color: #fff !important;
}

/* Eventos nativos do Vuetify, só um leve arredondamento */
.cal-wrap >>> .v-event,
.cal-wrap >>> .v-event-timed {
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
}

.cal-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.legend-item {
    display: inline-flex;
    align-items: center;
    font-size: 12.5px;
    color: var(--color-on-surface-variant);
}

.legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 6px;
    display: inline-block;
}

.detail-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
}

.detail-pill {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: var(--radius-pill);
    font-size: 12.5px;
    font-weight: 700;
    color: var(--pill-color);
    background: color-mix(in srgb, var(--pill-color) 12%, transparent);
}

.detail-date {
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    color: var(--color-on-surface-variant);
}

.detail-alert {
    display: flex;
    align-items: center;
    background: rgba(112, 42, 225, 0.06);
    border: 1px solid rgba(112, 42, 225, 0.16);
    padding: 9px 11px;
    border-radius: var(--radius-xs);
    font-size: 13px;
    color: var(--color-on-surface);
    margin-top: 4px;
}

.detail-alert--error {
    background: rgba(229, 72, 77, 0.07);
    border-color: rgba(229, 72, 77, 0.22);
}

.detail-alert--ok {
    background: rgba(16, 185, 129, 0.08);
    border-color: rgba(16, 185, 129, 0.22);
}

/* [admin] Painel de testes do fluxo automático */
.admin-test {
    border: 1px dashed var(--glass-panel-border, rgba(112, 105, 161, 0.35));
    border-radius: var(--radius-sm, 12px);
    padding: 10px 12px;
    background: var(--glass-soft-bg, rgba(255, 255, 255, 0.35));
}

.admin-test__title {
    display: flex;
    align-items: center;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--color-on-surface-variant);
    margin-bottom: 8px;
}

.admin-test__buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
</style>
